import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ProjectFileKind,
  ProjectFileSort,
  type ProjectFileKind as Kind,
  type ProjectFileSort as Sort,
} from '@scoutaround/script-breakdown-shared';

/**
 * THE FILES LIST'S STATE IS ITS ADDRESS: `?q=&kind=&sort=&view=grid&file=`.
 * A reload, a shared link and Back all land on the same view. Values are written
 * lower case for a readable address and read case-insensitively; a value outside
 * the vocabulary reads as absent rather than breaking the page, and the defaults
 * (newest first, the list) are never written. Every other parameter is kept.
 */

export type FilesView = 'list' | 'grid';

export interface FilesAddress {
  /** The open file. */
  readonly file: string | null;
  readonly q: string;
  readonly kind: Kind | null;
  readonly sort: Sort;
  readonly view: FilesView;
}

export type FilesAddressChange = {
  -readonly [K in keyof FilesAddress]?: FilesAddress[K] | null;
};

export function parseFilesAddress(params: URLSearchParams): FilesAddress {
  const kind = ProjectFileKind.safeParse((params.get('kind') ?? '').toUpperCase());
  const sort = ProjectFileSort.safeParse((params.get('sort') ?? '').toUpperCase());
  return {
    file: params.get('file') || null,
    q: params.get('q') ?? '',
    kind: kind.success ? kind.data : null,
    sort: sort.success ? sort.data : 'RECENT',
    view: params.get('view') === 'grid' ? 'grid' : 'list',
  };
}

/** Write one change into a copy of the parameters, dropping defaults so the address stays short. */
export function applyFilesChange(current: URLSearchParams, change: FilesAddressChange): URLSearchParams {
  const next = new URLSearchParams(current);
  const set = (key: string, value: string | null) => {
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  };

  if ('file' in change) set('file', change.file ?? null);
  if ('q' in change) set('q', change.q?.trim() ? change.q : null);
  if ('kind' in change) set('kind', change.kind ? change.kind.toLowerCase() : null);
  if ('sort' in change) set('sort', change.sort && change.sort !== 'RECENT' ? change.sort.toLowerCase() : null);
  if ('view' in change) set('view', change.view === 'grid' ? 'grid' : null);
  return next;
}

export function useFilesAddress() {
  const [params, setParams] = useSearchParams();
  const address = useMemo(() => parseFilesAddress(params), [params]);

  /**
   * `push` for a step somebody would walk back with Back (opening a file);
   * `replace` for refining what the list shows, so typing a search is not twenty
   * history entries.
   */
  const update = useCallback(
    (change: FilesAddressChange, options: { push?: boolean } = {}) => {
      setParams((current) => applyFilesChange(current, change), { replace: options.push !== true });
    },
    [setParams],
  );

  return { address, update };
}
